import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  apiFetch,
  // Sprint 10 — genera dieta AI
  UpdateAnthropometricsBody,
  GeneratePlanBody,
  GeneratePlanResponse,
  updatePatientAnthropometrics,
  generateDietPlan,
  AthletesResponse,
  CoachMeResponse,
  ProgramsResponse,
  RecoveryHeatmapResponse,
  TopPRResponse,
  VolumeWeekResponse,
  CoachAlertsResponse,
  CoachFeedResponse,
  LiveSessionResponse,
  AthleteRow,
  CoachProgramRow,
  ProgramFullResponse,
  CreateProgramBody,
  UpdateProgramBody,
  BulkSaveBody,
  CreateSessionBody,
  UpdateSessionBody,
  CreateExerciseBody,
  UpdateExerciseBody,
  createCoachProgram,
  getCoachProgramFull,
  updateCoachProgram,
  deleteCoachProgram,
  bulkSaveCoachProgram,
  createCoachSession,
  updateCoachSession,
  deleteCoachSession,
  createCoachExercise,
  updateCoachExercise,
  deleteCoachExercise,
  // Sprint 8.2 — appointments
  AppointmentResponse,
  AppointmentsListResponse,
  AppointmentWithPatient,
  AppointmentStatus,
  CreateAppointmentBody,
  UpdateAppointmentBody,
  ListAppointmentsParams,
  listAppointments,
  listAppointmentsToday,
  listAppointmentsUpcoming,
  getAppointment,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  setAppointmentStatus,
  // Sprint 9.3 — studio profile
  StudioProfile,
  UpdateProfileBody,
  UploadLogoResult,
  getStudioProfile,
  updateStudioProfile,
  uploadStudioLogo,
  deleteStudioLogo,
  // Sprint 9.2 — Pro Messaggi v2
  ProMessageThreadsResponse,
  ProMessageThreadResponse,
  ProMessageSendResponse,
  MarkReadResponse,
  listProMessageThreads,
  getProMessageThread,
  sendProMessage,
  markProMessageThreadRead,
  // Sprint 8.4 — Cal.com OAuth integration
  CalcomStatus,
  CalcomEventType,
  CalcomSyncResult,
  getCalcomStatus,
  getCalcomEventTypes,
  disconnectCalcom,
  syncCalcom,
  // Sprint 9.B — Diet plan editor wizard
  PlansListResponse,
  DietPlanRow,
  CreatePlanFromScratchBody,
  CreatePlanFromScratchResponse,
  ClonePlanBody,
  ClonePlanResponse,
  NutritionSearchResponse,
  MealSuggestSmartRequest,
  MealSuggestSmartResponse,
  MealSuggestSimpleRequest,
  MealSuggestSimpleResponse,
  listDietPlans,
  createPlanFromScratch,
  deleteDietPlan,
  cloneDietPlan,
  searchNutrition,
  suggestMealSmart,
  suggestMealSimple,
  // Sprint 9.B closure — edit mode + plan templates
  DietPlanDetail,
  UpdateDietPlanBody,
  UpdateDietPlanResponse,
  PlanTemplateRow,
  PlanTemplatesListResponse,
  PlanTemplateDetailResponse,
  CreatePlanTemplateBody,
  UpdatePlanTemplateBody,
  ApplyPlanTemplateBody,
  ApplyPlanTemplateResponse,
  getDietPlanDetail,
  updateDietPlan,
  listPlanTemplates,
  getPlanTemplate,
  createPlanTemplate,
  updatePlanTemplate,
  deletePlanTemplate,
  applyPlanTemplate,
  // Sprint 9.D — wizard creazione paziente
  CreatePatientBody,
  CreatePatientResponse,
  createPatient,
  // Sprint 9.5 — Analytics
  AnalyticsRange,
  AnalyticsOverview,
  RetentionResponse,
  AdherenceDistribution,
  AdherenceBucketDetailResponse,
  WeightTargetResponse,
  ConversionFunnel,
  FunnelStep,
  FunnelStepDetail,
  ScheduledReportsResponse,
  ScheduledReport,
  SavedViewsResponse,
  CreateScheduledReportBody,
  CreateSavedViewBody,
  UpsertWeightGoalBody,
  getAnalyticsOverview,
  getAnalyticsRetention,
  getAnalyticsAdherence,
  getAnalyticsAdherenceBucket,
  getAnalyticsWeightTarget,
  getAnalyticsFunnel,
  getAnalyticsFunnelStep,
  listAnalyticsScheduledReports,
  createAnalyticsScheduledReport,
  deleteAnalyticsScheduledReport,
  listAnalyticsSavedViews,
  createAnalyticsSavedView,
  deleteAnalyticsSavedView,
  upsertAnalyticsWeightGoal,
  // Sprint 10 Fase 3/F5 — subscription + consumo del piano
  MySubscription,
  getMySubscription,
} from './api';
import type { PatientsListResponse, PatientListItem } from './types';

// ---------------------------------------------------------------------------
// Nutri
// ---------------------------------------------------------------------------

export function usePatients() {
  return useQuery({
    queryKey: ['pro', 'patients'],
    queryFn: () => apiFetch<PatientsListResponse>('/api/pro/patients'),
    select: (data): PatientListItem[] => data.patients,
  });
}

// ---------------------------------------------------------------------------
// Coach
// ---------------------------------------------------------------------------

export function useCoachMe(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'me'],
    queryFn: () => apiFetch<CoachMeResponse>('/api/pro/coach/me'),
    enabled,
    staleTime: 30_000,
  });
}

export function useAthletes(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'athletes'],
    queryFn: () => apiFetch<AthletesResponse>('/api/pro/coach/athletes'),
    select: (d): AthleteRow[] => d.athletes,
    enabled,
    staleTime: 15_000,
  });
}

export function useLiveSession(athleteId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'live-session', athleteId],
    queryFn: () => apiFetch<LiveSessionResponse>(`/api/pro/coach/athlete/${athleteId}/live-session`),
    enabled: enabled && !!athleteId,
    refetchInterval: 10_000, // poll live every 10s
  });
}

export function useCoachPrograms(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'programs'],
    queryFn: () => apiFetch<ProgramsResponse>('/api/pro/coach/programs'),
    select: (d): CoachProgramRow[] => d.programs,
    enabled,
    staleTime: 60_000,
  });
}

export function useRecoveryHeatmap(days: number, enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'recovery', days],
    queryFn: () => apiFetch<RecoveryHeatmapResponse>(`/api/pro/coach/recovery-heatmap?days=${days}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useTopPR(days: number, enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'top-pr', days],
    queryFn: () => apiFetch<TopPRResponse>(`/api/pro/coach/top-pr?days=${days}`),
    enabled,
    staleTime: 60_000,
  });
}

export function useVolumeWeek(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'volume-week'],
    queryFn: () => apiFetch<VolumeWeekResponse>('/api/pro/coach/volume-week'),
    enabled,
    staleTime: 60_000,
  });
}

export function useCoachAlerts(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'alerts'],
    queryFn: () => apiFetch<CoachAlertsResponse>('/api/pro/coach/alerts'),
    enabled,
    staleTime: 30_000,
  });
}

export function useCoachFeed(enabled: boolean) {
  return useQuery({
    queryKey: ['pro', 'coach', 'feed'],
    queryFn: () => apiFetch<CoachFeedResponse>('/api/pro/coach/feed'),
    enabled,
    refetchInterval: 30_000, // live-ish
  });
}

// ---------------------------------------------------------------------------
// Sprint 7.2 — Editor wizard (queries + mutations)
// ---------------------------------------------------------------------------

export function useCoachProgramFull(programId: string | null, enabled = true) {
  return useQuery({
    queryKey: ['pro', 'coach', 'program-full', programId],
    queryFn: () => getCoachProgramFull(programId!),
    enabled: enabled && !!programId,
    staleTime: 10_000,
  });
}

function invalidateProgramsLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['pro', 'coach', 'programs'] });
  qc.invalidateQueries({ queryKey: ['pro', 'coach', 'me'] });
  qc.invalidateQueries({ queryKey: ['pro', 'coach', 'athletes'] });
}

export function useCreateCoachProgram() {
  const qc = useQueryClient();
  return useMutation<ProgramFullResponse, Error, CreateProgramBody>({
    mutationFn: (body) => createCoachProgram(body),
    onSuccess: () => invalidateProgramsLists(qc),
  });
}

export function useUpdateCoachProgram(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<ProgramFullResponse, Error, UpdateProgramBody>({
    mutationFn: (body) => {
      if (!programId) throw new Error('programId required');
      return updateCoachProgram(programId, body);
    },
    onSuccess: () => {
      invalidateProgramsLists(qc);
      if (programId) {
        qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
      }
    },
  });
}

export function useDeleteCoachProgram() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; archived: boolean }, Error, string>({
    mutationFn: (programId) => deleteCoachProgram(programId),
    onSuccess: () => invalidateProgramsLists(qc),
  });
}

export function useBulkSaveCoachProgram(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<ProgramFullResponse, Error, BulkSaveBody>({
    mutationFn: (body) => {
      if (!programId) throw new Error('programId required');
      return bulkSaveCoachProgram(programId, body);
    },
    onSuccess: () => {
      invalidateProgramsLists(qc);
      if (programId) {
        qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
      }
    },
  });
}

export function useCreateCoachSession(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ session: unknown }, Error, CreateSessionBody>({
    mutationFn: (body) => {
      if (!programId) throw new Error('programId required');
      return createCoachSession(programId, body);
    },
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

export function useUpdateCoachSession(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { sessionId: string; body: UpdateSessionBody }>({
    mutationFn: ({ sessionId, body }) => updateCoachSession(sessionId, body),
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

export function useDeleteCoachSession(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (sessionId) => deleteCoachSession(sessionId),
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

export function useCreateCoachExercise(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ exercise: unknown }, Error, { sessionId: string; body: CreateExerciseBody }>({
    mutationFn: ({ sessionId, body }) => createCoachExercise(sessionId, body),
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

export function useUpdateCoachExercise(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { exerciseId: string; body: UpdateExerciseBody }>({
    mutationFn: ({ exerciseId, body }) => updateCoachExercise(exerciseId, body),
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

export function useDeleteCoachExercise(programId: string | null) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (exerciseId) => deleteCoachExercise(exerciseId),
    onSuccess: () => {
      if (programId) qc.invalidateQueries({ queryKey: ['pro', 'coach', 'program-full', programId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 8.2 — Appointments queries & mutations
// ---------------------------------------------------------------------------

export function useAppointments(params: ListAppointmentsParams) {
  // Stable key: ordina campi per evitare cache busting per ordine.
  const key = ['pro', 'appointments', 'list', params.from, params.to, params.pro_role] as const;
  return useQuery<AppointmentsListResponse, Error>({
    queryKey: key,
    queryFn: () => listAppointments(params),
    staleTime: 15_000,
  });
}

export function useAppointmentsToday() {
  return useQuery<{ appointments: AppointmentWithPatient[] }, Error>({
    queryKey: ['pro', 'appointments', 'today'],
    queryFn: () => listAppointmentsToday(),
    staleTime: 15_000,
  });
}

export function useAppointmentsUpcoming(limit = 5) {
  return useQuery<{ appointments: AppointmentWithPatient[] }, Error>({
    queryKey: ['pro', 'appointments', 'upcoming', limit],
    queryFn: () => listAppointmentsUpcoming(limit),
    staleTime: 15_000,
  });
}

export function useAppointment(id: string | null) {
  return useQuery<AppointmentResponse, Error>({
    queryKey: ['pro', 'appointments', 'one', id],
    queryFn: () => getAppointment(id!),
    enabled: !!id,
    staleTime: 10_000,
  });
}

function invalidateAppointments(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['pro', 'appointments'] });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation<AppointmentResponse, Error, CreateAppointmentBody>({
    mutationFn: (body) => createAppointment(body),
    onSuccess: () => invalidateAppointments(qc),
  });
}

export function useUpdateAppointment() {
  const qc = useQueryClient();
  return useMutation<AppointmentResponse, Error, { id: string; body: UpdateAppointmentBody }>({
    mutationFn: ({ id, body }) => updateAppointment(id, body),
    onSuccess: () => invalidateAppointments(qc),
  });
}

export function useDeleteAppointment() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; canceled: boolean }, Error, string>({
    mutationFn: (id) => deleteAppointment(id),
    onSuccess: () => invalidateAppointments(qc),
  });
}

export function useSetAppointmentStatus() {
  const qc = useQueryClient();
  return useMutation<AppointmentResponse, Error, { id: string; status: AppointmentStatus }>({
    mutationFn: ({ id, status }) => setAppointmentStatus(id, status),
    onSuccess: () => invalidateAppointments(qc),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.3 — Studio profile (Impostazioni)
// ---------------------------------------------------------------------------

export function useStudioProfile() {
  return useQuery<StudioProfile, Error>({
    queryKey: ['pro', 'profile'],
    queryFn: () => getStudioProfile(),
    staleTime: 30_000,
  });
}

function invalidateProfileAndAuth(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['pro', 'profile'] });
  // /api/auth/me espone studio_name + pro_kind → invalida lo store auth via refresh
  // Il consumer (Impostazioni) chiamerà auth.refresh() esplicitamente, qui solo invalida
  // l'eventuale chiave dedicata se in futuro la spostiamo in react-query.
}

export function useUpdateStudioProfile() {
  const qc = useQueryClient();
  return useMutation<StudioProfile, Error, UpdateProfileBody>({
    mutationFn: (body) => updateStudioProfile(body),
    onSuccess: (data) => {
      qc.setQueryData(['pro', 'profile'], data);
      invalidateProfileAndAuth(qc);
    },
  });
}

export function useUploadStudioLogo() {
  const qc = useQueryClient();
  return useMutation<UploadLogoResult, Error, File>({
    mutationFn: (file) => uploadStudioLogo(file),
    onSuccess: () => {
      invalidateProfileAndAuth(qc);
    },
  });
}

export function useDeleteStudioLogo() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, void>({
    mutationFn: () => deleteStudioLogo(),
    onSuccess: () => {
      invalidateProfileAndAuth(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.2 — Pro Messaggi v2 queries & mutations
// Polling: thread list 10s. Thread aperto 10s. Quando l'utente invia,
// mark-read avviene immediatamente lato server (POST) e poi optimistic.
// ---------------------------------------------------------------------------

export function useProMessageThreads() {
  return useQuery<ProMessageThreadsResponse, Error>({
    queryKey: ['pro', 'messages', 'threads'],
    queryFn: () => listProMessageThreads(),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
}

export function useProMessageThread(patientId: string | null, limit = 50) {
  return useQuery<ProMessageThreadResponse, Error>({
    queryKey: ['pro', 'messages', 'thread', patientId, limit],
    queryFn: () => getProMessageThread({ patientId: patientId!, limit }),
    enabled: !!patientId,
    refetchInterval: 10_000,
    staleTime: 2_000,
  });
}

export function useSendProMessage(patientId: string | null) {
  const qc = useQueryClient();
  return useMutation<ProMessageSendResponse, Error, string>({
    mutationFn: (text) => {
      if (!patientId) throw new Error('patientId required');
      return sendProMessage(patientId, text);
    },
    onSuccess: () => {
      if (patientId) {
        qc.invalidateQueries({ queryKey: ['pro', 'messages', 'thread', patientId] });
      }
      qc.invalidateQueries({ queryKey: ['pro', 'messages', 'threads'] });
    },
  });
}

export function useMarkProMessageThreadRead() {
  const qc = useQueryClient();
  return useMutation<MarkReadResponse, Error, string>({
    mutationFn: (patientId) => markProMessageThreadRead(patientId),
    onSuccess: (_, patientId) => {
      qc.invalidateQueries({ queryKey: ['pro', 'messages', 'thread', patientId] });
      qc.invalidateQueries({ queryKey: ['pro', 'messages', 'threads'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 8.4 — Cal.com OAuth integration
// Polling status ogni 30s mentre la pagina Impostazioni è aperta.
// ---------------------------------------------------------------------------

export function useCalcomStatus() {
  return useQuery<CalcomStatus, Error>({
    queryKey: ['pro', 'calcom', 'status'],
    queryFn: () => getCalcomStatus(),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useCalcomEventTypes(enabled: boolean) {
  return useQuery<{ event_types: CalcomEventType[] }, Error>({
    queryKey: ['pro', 'calcom', 'event-types'],
    queryFn: () => getCalcomEventTypes(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useDisconnectCalcom() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, void>({
    mutationFn: () => disconnectCalcom(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'calcom'] });
      qc.invalidateQueries({ queryKey: ['pro', 'appointments'] });
    },
  });
}

export function useSyncCalcom() {
  const qc = useQueryClient();
  return useMutation<CalcomSyncResult, Error, { from?: string; to?: string } | void>({
    mutationFn: (range) => syncCalcom(range || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'calcom', 'status'] });
      qc.invalidateQueries({ queryKey: ['pro', 'appointments'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.B — Diet plan editor wizard queries & mutations
// ---------------------------------------------------------------------------

export function useDietPlans(enabled = true) {
  return useQuery<PlansListResponse, Error, DietPlanRow[]>({
    queryKey: ['pro', 'plans', 'list'],
    queryFn: () => listDietPlans(),
    select: (d) => d.plans,
    enabled,
    staleTime: 15_000,
  });
}

function invalidatePlansLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['pro', 'plans'] });
  qc.invalidateQueries({ queryKey: ['pro', 'patients'] });
}

export function useCreateDietPlan() {
  const qc = useQueryClient();
  return useMutation<CreatePlanFromScratchResponse, Error, CreatePlanFromScratchBody>({
    mutationFn: (body) => createPlanFromScratch(body),
    onSuccess: () => invalidatePlansLists(qc),
  });
}

export function useDeleteDietPlan() {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, string>({
    mutationFn: (planId) => deleteDietPlan(planId),
    onSuccess: () => invalidatePlansLists(qc),
  });
}

export function useCloneDietPlan() {
  const qc = useQueryClient();
  return useMutation<ClonePlanResponse, Error, { sourceId: string; body: ClonePlanBody }>({
    mutationFn: ({ sourceId, body }) => cloneDietPlan(sourceId, body),
    onSuccess: () => invalidatePlansLists(qc),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.B closure — Hooks edit mode + Plan templates
// ---------------------------------------------------------------------------

export function useDietPlan(planId: string | null | undefined) {
  return useQuery<DietPlanDetail, Error>({
    queryKey: ['pro', 'plans', 'detail', planId],
    queryFn: () => getDietPlanDetail(planId as string),
    enabled: !!planId,
    staleTime: 0, // edit mode: sempre fresco al mount
  });
}

export function useUpdateDietPlan() {
  const qc = useQueryClient();
  return useMutation<UpdateDietPlanResponse, Error, { planId: string; body: UpdateDietPlanBody }>({
    mutationFn: ({ planId, body }) => updateDietPlan(planId, body),
    onSuccess: (_, vars) => {
      invalidatePlansLists(qc);
      qc.invalidateQueries({ queryKey: ['pro', 'plans', 'detail', vars.planId] });
    },
  });
}

function invalidateTemplatesLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['pro', 'plan-templates'] });
}

export function usePlanTemplates() {
  return useQuery<PlanTemplatesListResponse, Error, PlanTemplateRow[]>({
    queryKey: ['pro', 'plan-templates', 'list'],
    queryFn: () => listPlanTemplates(),
    select: (d) => d.templates,
    staleTime: 30_000,
  });
}

export function usePlanTemplate(id: string | null | undefined) {
  return useQuery<PlanTemplateDetailResponse, Error>({
    queryKey: ['pro', 'plan-templates', 'detail', id],
    queryFn: () => getPlanTemplate(id as string),
    enabled: !!id,
    staleTime: 0,
  });
}

export function useCreatePlanTemplate() {
  const qc = useQueryClient();
  return useMutation<{ id: string }, Error, CreatePlanTemplateBody>({
    mutationFn: (body) => createPlanTemplate(body),
    onSuccess: () => invalidateTemplatesLists(qc),
  });
}

export function useUpdatePlanTemplate() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { id: string; body: UpdatePlanTemplateBody }>({
    mutationFn: ({ id, body }) => updatePlanTemplate(id, body),
    onSuccess: () => invalidateTemplatesLists(qc),
  });
}

export function useDeletePlanTemplate() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => deletePlanTemplate(id),
    onSuccess: () => invalidateTemplatesLists(qc),
  });
}

export function useApplyPlanTemplate() {
  const qc = useQueryClient();
  return useMutation<ApplyPlanTemplateResponse, Error, { id: string; body: ApplyPlanTemplateBody }>({
    mutationFn: ({ id, body }) => applyPlanTemplate(id, body),
    onSuccess: () => {
      invalidatePlansLists(qc);
      invalidateTemplatesLists(qc);
    },
  });
}

export function useCreatePatient() {
  const qc = useQueryClient();
  return useMutation<CreatePatientResponse, Error, CreatePatientBody>({
    mutationFn: (body) => createPatient(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'patients'] });
    },
  });
}

export function useNutritionSearch(query: string, enabled: boolean) {
  return useQuery<NutritionSearchResponse, Error>({
    queryKey: ['nutrition', 'search', query],
    queryFn: () => searchNutrition(query, 10),
    enabled: enabled && query.trim().length >= 2,
    staleTime: 60_000,
  });
}

export function useSuggestMealSmart() {
  return useMutation<MealSuggestSmartResponse, Error, MealSuggestSmartRequest>({
    mutationFn: (body) => suggestMealSmart(body),
  });
}

export function useSuggestMealSimple() {
  return useMutation<MealSuggestSimpleResponse, Error, MealSuggestSimpleRequest>({
    mutationFn: (body) => suggestMealSimple(body),
  });
}

// ---------------------------------------------------------------------------
// Sprint 9.5 — Analytics queries & mutations
// Stale time aggressivo (5min) per matching cache server-side.
// Invalidation manuale via refetch o quando muta un goal.
// ---------------------------------------------------------------------------

export function useAnalyticsOverview(range: AnalyticsRange = '30d', from?: string | null, to?: string | null) {
  return useQuery<AnalyticsOverview, Error>({
    queryKey: ['pro', 'analytics', 'overview', range, from || null, to || null],
    queryFn: () => getAnalyticsOverview(range, from || null, to || null),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsRetention() {
  return useQuery<RetentionResponse, Error>({
    queryKey: ['pro', 'analytics', 'retention'],
    queryFn: () => getAnalyticsRetention(),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsAdherence(range: AnalyticsRange = '30d', from?: string | null, to?: string | null) {
  return useQuery<AdherenceDistribution, Error>({
    queryKey: ['pro', 'analytics', 'adherence', range, from || null, to || null],
    queryFn: () => getAnalyticsAdherence(range, from || null, to || null),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsAdherenceBucket(
  label: string | null,
  range: AnalyticsRange = '30d',
  from?: string | null,
  to?: string | null,
) {
  return useQuery<AdherenceBucketDetailResponse, Error>({
    queryKey: ['pro', 'analytics', 'adherence-bucket', label, range, from || null, to || null],
    queryFn: () => {
      if (!label) throw new Error('label required');
      return getAnalyticsAdherenceBucket(label, range, from || null, to || null);
    },
    enabled: !!label,
    staleTime: 60_000,
  });
}

export function useAnalyticsWeightTarget() {
  return useQuery<WeightTargetResponse, Error>({
    queryKey: ['pro', 'analytics', 'weight-target'],
    queryFn: () => getAnalyticsWeightTarget(),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsFunnel() {
  return useQuery<ConversionFunnel, Error>({
    queryKey: ['pro', 'analytics', 'funnel'],
    queryFn: () => getAnalyticsFunnel(),
    staleTime: 5 * 60_000,
  });
}

export function useAnalyticsFunnelStep(step: FunnelStep | null) {
  return useQuery<FunnelStepDetail, Error>({
    queryKey: ['pro', 'analytics', 'funnel-step', step],
    queryFn: () => {
      if (!step) throw new Error('step required');
      return getAnalyticsFunnelStep(step);
    },
    enabled: !!step,
    staleTime: 60_000,
  });
}

export function useAnalyticsScheduledReports() {
  return useQuery<ScheduledReportsResponse, Error>({
    queryKey: ['pro', 'analytics', 'scheduled'],
    queryFn: () => listAnalyticsScheduledReports(),
    staleTime: 30_000,
  });
}

export function useCreateAnalyticsScheduledReport() {
  const qc = useQueryClient();
  return useMutation<{ report: ScheduledReport }, Error, CreateScheduledReportBody>({
    mutationFn: (body) => createAnalyticsScheduledReport(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'scheduled'] });
    },
  });
}

export function useDeleteAnalyticsScheduledReport() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => deleteAnalyticsScheduledReport(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'scheduled'] });
    },
  });
}

export function useAnalyticsSavedViews() {
  return useQuery<SavedViewsResponse, Error>({
    queryKey: ['pro', 'analytics', 'views'],
    queryFn: () => listAnalyticsSavedViews(),
    staleTime: 60_000,
  });
}

export function useCreateAnalyticsSavedView() {
  const qc = useQueryClient();
  return useMutation<{ view: unknown }, Error, CreateSavedViewBody>({
    mutationFn: (body) => createAnalyticsSavedView(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'views'] });
    },
  });
}

export function useDeleteAnalyticsSavedView() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => deleteAnalyticsSavedView(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'views'] });
    },
  });
}

export function useUpsertAnalyticsWeightGoal() {
  const qc = useQueryClient();
  return useMutation<{ goal: unknown }, Error, { patientId: string; body: UpsertWeightGoalBody }>({
    mutationFn: ({ patientId, body }) => upsertAnalyticsWeightGoal(patientId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'weight-target'] });
      qc.invalidateQueries({ queryKey: ['pro', 'analytics', 'overview'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 10 — Genera dieta AI mutations
// ---------------------------------------------------------------------------

export function useUpdateAnthropometrics() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, { patientId: string; body: UpdateAnthropometricsBody }>({
    mutationFn: ({ patientId, body }) => updatePatientAnthropometrics(patientId, body),
    onSuccess: (_, vars) => {
      // Invalida il detail del paziente così eventuali altri consumer aggiornano
      qc.invalidateQueries({ queryKey: ['pro', 'patients'] });
      qc.invalidateQueries({ queryKey: ['pro', 'patient', vars.patientId] });
    },
  });
}

export function useGenerateDietPlan() {
  const qc = useQueryClient();
  return useMutation<GeneratePlanResponse, Error, GeneratePlanBody>({
    mutationFn: (body) => generateDietPlan(body),
    onSuccess: () => {
      // Il piano è ora nel DB: invalida la lista piani
      invalidatePlansLists(qc);
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 10 Fase 3/F5 — Subscription + consumo del piano (badge Impostazioni)
// ---------------------------------------------------------------------------

export function useMySubscription() {
  return useQuery<MySubscription, Error>({
    queryKey: ['billing', 'me'],
    queryFn: () => getMySubscription(),
    staleTime: 30_000,
  });
}
